/**
 * MapControls: Helper functions for map interaction and control
 */
import { MutableRefObject } from 'react';
import { logDebug } from './utils';
import { minZoom, displayBounds } from './constants';

/**
 * Setup map with appropriate settings and boundaries
 */
export const setupMap = (
  map: google.maps.Map,
  ringRoadBounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  },
  serviceAreaCoordinates: google.maps.LatLngLiteral[]
) => {
  // Create a polygon for the 2nd ring road area (service area)
  // This visually shows users where our delivery service is available
  const ringRoadPolygon = new google.maps.Polygon({
    paths: serviceAreaCoordinates,
    strokeColor: "#4CAF50", // Green outline for better visibility
    strokeOpacity: 0.9,
    strokeWeight: 4,
    fillColor: "#22C55E", // Green fill
    fillOpacity: 0.15, // More visible
    map: map
  });
  
  // Set wider bounds for the map using displayBounds
  const bounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(displayBounds.south, displayBounds.west),
    new google.maps.LatLng(displayBounds.north, displayBounds.east)
  );
  
  // Change map settings to ensure it's draggable with wider bounds
  map.setOptions({
    restriction: {
      latLngBounds: bounds,
      strictBounds: false
    },
    minZoom: minZoom, // Allow zooming out further
    draggable: true,
    keyboardShortcuts: false
  });
  
  // Add explicit drag listeners for debugging
  map.addListener('dragstart', () => {
    logDebug('Map drag started');
  });
  
  map.addListener('drag', () => {
    const currentCenter = map.getCenter();
    if (currentCenter) {
      logDebug('Map dragging, current center:', {
        lat: currentCenter.lat(),
        lng: currentCenter.lng()
      });
    }
  });
  
  map.addListener('dragend', () => {
    logDebug('Map drag ended');
    const currentCenter = map.getCenter();
    if (currentCenter) {
      logDebug('New map center after drag:', {
        lat: currentCenter.lat(),
        lng: currentCenter.lng()
      });
    }
  });
};

/**
 * Handle map idle listener registration and removal
 */
export const handleIdleListeners = (
  map: google.maps.Map | null,
  idleListenerRef: MutableRefObject<google.maps.MapsEventListener | null>,
  handleMapMoveEnd: () => void
) => {
  // Add idle listener with safeguards
  const addIdleListener = () => {
    if (!map) return;
    
    // Remove existing listener if any
    if (idleListenerRef.current) {
      google.maps.event.removeListener(idleListenerRef.current);
      idleListenerRef.current = null;
    }
    
    // Add fresh listener
    idleListenerRef.current = map.addListener('idle', handleMapMoveEnd);
    logDebug('Added idle listener for map movements');
  };
  
  // Remove idle listener safely
  const removeIdleListener = () => {
    if (idleListenerRef.current) {
      google.maps.event.removeListener(idleListenerRef.current);
      idleListenerRef.current = null;
      logDebug('Removed idle listener');
    }
  };
  
  return { addIdleListener, removeIdleListener };
};

/**
 * Show a toast notification on the map
 */
export const showMapToast = (
  mapRef: MutableRefObject<google.maps.Map | null>,
  message: string,
  duration: number = 3000
) => {
  const mapContainer = mapRef.current?.getDiv();
  if (!mapContainer) return;
  
  const toast = document.createElement('div');
  toast.className = 'map-ready-toast';
  toast.style.position = 'absolute';
  toast.style.bottom = '100px';
  toast.style.left = '50%';
  toast.style.transform = 'translateX(-50%)';
  toast.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  toast.style.color = 'white';
  toast.style.padding = '8px 16px';
  toast.style.borderRadius = '20px';
  toast.style.fontSize = '14px';
  toast.style.zIndex = '50';
  toast.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
  toast.textContent = message;
  
  mapContainer.appendChild(toast);
  
  // Fade out and remove after specified duration
  setTimeout(() => {
    toast.style.transition = 'opacity 0.5s ease';
    toast.style.opacity = '0';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 500);
  }, duration);
}; 